// src/scenes/welcome/index.jsx
import { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";

import { API_BASE_URL } from "../../config";
import { startDemoMode, stopDemoMode } from "../../demoMode";
import useToast from "../../components/useToast";
import AppButton from "../../design-system/components/AppButton";
import AppCard from "../../design-system/components/AppCard";
import AppCardFooter from "../../design-system/components/AppCardFooter";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppText from "../../design-system/components/AppText";

const ChoicePanel = ({ icon, title, body, action }) => (
  <Box
    component={AppCard}
    sx={{
      width: "100%",
      maxWidth: 420,
    }}
  >
    <Box component={AppCardHeader} sx={{ flex: "1 1 auto" }}>
      <Stack spacing={2} alignItems="flex-start">
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            display: "grid",
            placeItems: "center",
            backgroundColor: "rgba(26, 204, 169, 0.12)",
            color: "#0B8F78",
          }}
        >
          {icon}
        </Box>
        <Box>
          <AppText as="div" variant="heading-3">
            {title}
          </AppText>
          <AppText as="div" tone="secondary">
            {body}
          </AppText>
        </Box>
      </Stack>
    </Box>
    <AppCardFooter>
      <AppButton onClick={action.onClick} disabled={action.disabled}>
        {action.label}
      </AppButton>
    </AppCardFooter>
  </Box>
);

const Welcome = () => {
  const [initialSetupNeeded, setInitialSetupNeeded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState("");
  const [isStartingDemo, setIsStartingDemo] = useState(false);
  const [searchParams] = useSearchParams();
  const { error } = useToast();

  const token = localStorage.getItem("session_token");
  const navigate = useNavigate();
  const forceChoice = searchParams.get("choose") === "1";

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setIsLoading(true);
        const [initRes, userRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/web/check_initial_setup`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_BASE_URL}/api/web/current_user`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!mounted) return;
        setInitialSetupNeeded(Boolean(initRes.data.initialSetupNeeded));
        setAccessLevel(userRes.data.access_level);
      } catch (error) {
        console.error("Error loading initial setup state:", error);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    if (isLoading || forceChoice || !accessLevel) return;

    if (accessLevel === "User") {
      navigate("/shiftplan");
      return;
    }

    if (!initialSetupNeeded) {
      navigate("/admin/dashboard");
    }
  }, [accessLevel, forceChoice, initialSetupNeeded, isLoading, navigate]);

  const startDemo = async () => {
    setIsStartingDemo(true);
    try {
      await startDemoMode();
      navigate("/admin/dashboard");
    } catch (startError) {
      console.error("Error starting demo mode:", startError);
      error("Die Demo-Version konnte nicht gestartet werden. Bitte versuche es erneut.");
      setIsStartingDemo(false);
    }
  };

  const startSetup = () => {
    stopDemoMode();
    navigate("/initial-setup");
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#F6F8FA",
        p: { xs: 2, md: 4 },
      }}
    >
      {/* Waehrend des Ladens bewusst KEIN Loader: /welcome ist nur eine
          Durchgangsstation, die anhand von access_level / initialSetupNeeded
          weiterleitet. Ein eigener Loader wuerde hier als Vollbild-Overlay ohne
          Sidebar/Topbar erscheinen und beim Sprung ins Dashboard-Layout durch den
          dortigen Loader abgeloest — es soll nur die Ladeanzeige der Zielseite
          sichtbar sein. */}
      {isLoading ? null : (
        <Stack
          spacing={{ xs: 3, md: 4 }}
          alignItems="center"
          sx={{ width: "100%", maxWidth: 980, transform: "translateY(-15%)" }}
        >
          <Box
            component="img"
            src="/assets/TimeTab_Logo_quer_cmyk_pos.png"
            alt="TimeTab Logo"
            sx={{ width: { xs: 180, md: 240 }, height: "auto" }}
          />

          <Box textAlign="center" sx={{ maxWidth: 720 }}>
            <AppText as="div" role="heading" aria-level="1" variant="heading-0">
              Willkommen bei TimeTab
            </AppText>
            <AppText as="div" tone="secondary">
              Wähle deinen Startpunkt selber. Nutze die Demo, um TimeTab mit Beispieldaten kennenzulernen oder starte direkt mit der Einrichtung deiner Firma.
            </AppText>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="stretch"
            justifyContent="center"
            sx={{ width: "100%" }}
          >
            <ChoicePanel
              icon={<ExploreOutlinedIcon />}
              title="Demo ausprobieren"
              body="Teste TimeTab mit Beispieldaten, um die Möglichkeiten auszutesten. Die Daten dienen nur als Vorschau und können nicht gespeichert werden."
              action={{
                label: isStartingDemo ? "Demo wird gestartet…" : "Demo starten",
                onClick: startDemo,
                disabled: isStartingDemo,
              }}
            />
            <ChoicePanel
              icon={<TuneOutlinedIcon />}
              title="Direkt einrichten"
              body="Lege deine Unternehmen mit nur wenigen Informationen an. Danach kannst du mit der Planung starten."
              action={{ label: "Einrichtung starten", onClick: startSetup }}
            />
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default Welcome;
